<template>
  <div class="form-block">
    <!-- Generated coupon data -->
    <transition name="fade" type="out-in">
      <div v-show="isSuccessResult" class="result-block">
        <div class="result-block-options">
          <div class="result-block-title">Coupon - {{ createdCoupon.id }}</div>

          <el-tooltip content="Close current block" placement="top" effect="light">
            <el-button @click="isSuccessResult = false" class="close-result-block" icon="mdi mdi-close" size="mini"/>
          </el-tooltip>
        </div>

        <div class="result-block-row">
          <div class="result-block-column">
            <div class="result-item">
              <span class="label">Plan group</span>
              <el-tag class="value" type="info">{{ createdCoupon.plan ? createdCoupon.plan : 'All' }}</el-tag>
            </div>

            <div class="result-item">
              <span class="label">Plan type</span>
              <el-tag class="value" type="info">{{ createdCoupon.type ? createdCoupon.type : '---' }}</el-tag>
            </div>

            <div class="result-item">
              <span class="label">Discount</span>
              <el-tag class="value" type="info">{{ createdCoupon.discount }}</el-tag>
            </div>
          </div>

          <div class="result-block-column">
            <div class="result-item">
              <span class="label">Active</span>
              <el-tag class="value" type="info">{{ createdCoupon.active }}</el-tag>
            </div>

            <div class="result-item">
              <span class="label">Include UTM</span>
              <el-tag class="value" type="info">{{ createdCoupon.includeUtm }}</el-tag>
            </div>

            <div class="result-item">
              <span class="label">Expired At</span>
              <el-tag class="value" type="info">{{ createdCoupon.expiredAt }}</el-tag>
            </div>
          </div>

          <div class="result-block-column">
            <div class="result-item">
              <span class="label">Code</span>
              <el-input v-model="createdCoupon.code" size="mini" disabled style="width: 100px"/>
              <CopyButton label="Coupon Code" :target="createdCoupon.code" />
            </div>

            <div class="result-item">
              <span class="label">ID</span>
              <el-input v-model="createdCoupon.id" size="mini" disabled style="width: 275px"/>
              <CopyButton label="Coupon ID" :target="createdCoupon.id" />
            </div>

            <div class="result-item">
              <span class="label">Link</span>
              <CopyButton label="Coupon Link" content="Coupon Link" :target="createdCoupon.link" style="padding-left: 0;font-size: 14px"/>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <el-form
        size="small"
        :model="form"
        :rules="validateRules"
        ref="generateCouponForm"
        class="coupon-form"
        :label-position="'left'"
        :label-width="'175px'">

      <!-- Coupon Settings columns -->
      <el-row class="settings-row">
        <div class="settings-row-block">

          <!-- Discount switcher -->

          <div class="settings-row-block__item">
            <el-form-item class="item-switch">
              <span slot="label" class="item-label">Discount Settings</span>
              <el-switch
                  size="medium"
                  v-model="form.include_coupon"
                  active-color="#13ce66"
                  inactive-color="#ff4949"
              />
            </el-form-item>
          </div>

          <!-- Coupon Plan group select -->
          <div class="settings-row-block__item">
            <el-form-item label="Plan">
              <span slot="label" class="item-label">Plan group</span>
              <el-select
                  size="small"
                  class="form-item-select"
                  @change="handlePlanGroupChange"
                  v-model="form.plan_group_id"
                  :disabled="!form.include_coupon"
              >
                <el-option
                    v-for="plan in planGroupsList"
                    :key="plan.value"
                    :label="plan.label"
                    :value="plan.value"
                />
              </el-select>
            </el-form-item>
          </div>

          <!-- Coupon Plan type select -->
          <div class="settings-row-block__item">
            <el-form-item prop="type">
              <span slot="label" class="item-label">Plan type</span>
              <el-select
                  size="small"
                  class="form-item-select"
                  v-model="form.plan_type_id"
                  :disabled="!form.include_coupon"
              >
                <el-option
                    v-for="type in planTypesByGroup"
                    :key="type.value"
                    :label="type.label"
                    :value="type.value"
                />
              </el-select>
            </el-form-item>
          </div>

          <!-- Coupon expired at datepicker -->
          <div class="settings-row-block__item">
            <el-form-item prop="expired_at">
              <span slot="label" class="item-label">Expired at</span>
              <el-date-picker
                  size="small"
                  v-model="form.expired_at"
                  :picker-options="datePickerOptions"
                  type="date" style="width: 145px"
                  format="MM-dd-yyyy"
                  value-format="yyyy-MM-dd"
                  placeholder="Select Expired Date"
              />
            </el-form-item>
          </div>

          <!-- Coupon reuse counter -->
          <div class="settings-row-block__item">
            <el-form-item>
              <span slot="label" class="item-label">Reuse count</span>
              <el-input-number v-model="form.available_count" :min="1" :max="100" :disabled="!form.include_coupon" size="small" style="width: 145px"/>
            </el-form-item>
          </div>

          <!-- Coupon Discount select -->
          <div class="settings-row-block__item">
            <el-form-item>
              <span slot="label" class="item-label">Discount</span>
              <el-select style="width: 225px"
                  v-model="form.discount_id"
                  :disabled="!form.include_coupon"
              >
                <el-option
                    v-for="option in discounSelectList"
                    :key="option.label"
                    :label="option.label"
                    :value="option.value"
                />
              </el-select>
            </el-form-item>
          </div>

          <!-- Coupon URL input -->
          <div class="settings-row-block__item">
            <el-form-item prop="url">
              <span slot="label" class="item-label">Url</span>
              <el-input v-model="form.url" class="form-item-input" placeholder="http://example.com" size="small" />
            </el-form-item>
          </div>

          <!-- Coupon URN input -->
          <div class="settings-row-block__item">
            <el-form-item prop="urn">
              <span slot="label" class="item-label">Urn</span>
              <el-input v-model="form.urn" class="form-item-input" placeholder="/example/" size="small" />
            </el-form-item>
          </div>
        </div>

        <div class="settings-row-block">

          <!-- Coupon link UTM tags switcher -->
          <div class="settings-row-block__item">
            <el-form-item class="item-switch">
              <span slot="label" class="item-label">UTM Settings</span>
              <el-switch
                  size="small"
                  v-model="form.include_utm"
                  active-color="#13ce66"
                  inactive-color="#ff4949"
              />
            </el-form-item>
          </div>

          <!-- utm_funnel input -->
          <div class="settings-row-block__item">
            <el-form-item prop="utm_funnel">
              <span slot="label" class="item-label">Funnel</span>
              <el-input
                  v-model="form.utm.utm_funnel"
                  :disabled="!form.include_utm"
                  class="form-item-input"
                  placeholder="utm_funnel" size="small"/>
            </el-form-item>
          </div>

          <!-- utm_source input -->
          <div class="settings-row-block__item">
            <el-form-item prop="utm_source">
              <span slot="label" class="item-label">Source</span>
              <el-input v-model="form.utm.utm_source" :disabled="!form.include_utm" class="form-item-input" placeholder="utm_source" size="small"/>
            </el-form-item>
          </div>

          <!-- utm_medium input -->
          <div class="settings-row-block__item">
            <el-form-item prop="utm_medium">
              <span slot="label" class="item-label">Medium</span>
              <el-input v-model="form.utm.utm_medium" :disabled="!form.include_utm" class="form-item-input" placeholder="utm_medium" size="small"/>
            </el-form-item>
          </div>

          <!-- utm_campaign input -->
          <div class="settings-row-block__item">
            <el-form-item prop="utm_campaign">
              <span slot="label" class="item-label">Campaign</span>
              <el-input v-model="form.utm.utm_campaign" :disabled="!form.include_utm" class="form-item-input" placeholder="utm_campaign" size="small"/>
            </el-form-item>
          </div>

          <!-- utm_content input -->
          <div class="settings-row-block__item">
            <el-form-item prop="utm_content">
              <span slot="label" class="item-label">Content</span>
              <el-input v-model="form.utm.utm_content" :disabled="!form.include_utm" class="form-item-input" placeholder="utm_content" size="small" />
            </el-form-item>
          </div>

            <!-- utm_term input -->
          <div class="settings-row-block__item">
            <el-form-item prop="utm_term">
              <span slot="label" class="item-label">Term</span>
              <el-input v-model="form.utm.utm_term" :disabled="!form.include_utm" class="form-item-input" placeholder="utm_term" size="small" />
            </el-form-item>
          </div>
        </div>
      </el-row>

      <!-- Form submit button-->
      <el-row class="submit-row">
        <div class="submit-row-block">
          <div class="submit-row-block__item">
            <el-button
                :loading="isSubmitLoading"
                @click="generateCouponHandler"
                icon="mdi mdi-auto-fix"
                type="primary" round plain
                class="submit-btn"
            >
              Generate
            </el-button>
          </div>
        </div>
      </el-row>
    </el-form>
  </div>
</template>

<script>
  import discountCoupons from '@/mixins/discountCoupons'
  import CopyButton from '@/components/custom/CopyButton';

  export default {
    name: 'GenerateCouponForm',
    mixins: [discountCoupons],
    components: {
      CopyButton
    },
    data() {
      const planTypeRequires = (rule, value, callback) => (this.form.plan_group_id !== null && this.form.plan_type_id === null) ? callback('Coupon plan type is required') : callback()
      const checkUrl = (rule, value, callback) => /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/gm.test(value) ? callback() : callback('Incorrect URL format')

      return {
        validateRules: {
          expired_at: [{ required: true, message: 'Coupon expired date is required' }],
          url: [{ required: true, message: 'Url is required' }, { validator: checkUrl }],
          type: [{ validator: planTypeRequires }]
        },
        form: {
          url: `${process.env.VUE_APP_MAIN_SITE}`,
          urn: '',
          code: '',
          active: true,
          include_utm: true,
          include_coupon: true,
          available_count: 1,
          plan_group_id: null,
          plan_type_id: null,
          expired_at: '',
          discount_id: 1,
          utm: {
            utm_funnel: '',
            utm_source: '',
            utm_campaign: '',
            utm_medium: '',
            utm_term: '',
            utm_content: ''
          }
        },
        createdCoupon: {
          id: '',
          code: '',
          link: '',
          plan: '',
          type: '',
          discount: '',
          active: '',
          expiredAt: '',
          includeUtm: ''
        },
        isSubmitLoading: false,
        isSuccessResult: false
      }
    },
    methods: {
      generateCouponHandler() {
        this.$refs.generateCouponForm.validate(valid => {
          if (valid) {
            this.isSuccessResult = false
            this.isSubmitLoading = true

            this.form.include_utm = Object.values(this.form.utm).some(tag => !!tag)
            this.$store.dispatch('discountCoupons/createCoupon', this.form)
                .then(response => {
                  this.isSuccessResult = true
                  this.$notify.success('Coupon created')

                  this.couponResponseResult(response)
                  this.resetFormHandler()
                })
                .catch(error => {
                  this.isSuccessResult = false

                  this.$notify.error({ title: error.message, message: Object.values(error.errors)[0][0] })
                })
                .finally(() => this.isSubmitLoading = false)
          } return false
        })
      },
      couponResponseResult(res) {
        this.createdCoupon = {
          id: res.data.id,
          code: res.data.code,
          link: res.data.link,
          plan: res.data.plan_group_name,
          type: res.data.plan_type_name,
          discount: res.data.discount_name,
          active: res.data.active,
          expiredAt: res.data.expired_at,
          includeUtm: res.data.include_utm
        }
      },
      resetFormHandler() {
        this.$refs['generateCouponForm'].resetFields();
        this.form.active = true
        this.form.include_utm = true
        this.form.include_coupon = true
        this.form.available_count = 1
        this.form.plan_group_id = null
        this.form.plan_type_id = null
        this.form.discount_id = 1
        this.form.urn = null
        this.form.url = `${process.env.VUE_APP_MAIN_SITE}`
        this.form.utm = {
          utm_funnel: null,
          utm_source: null,
          utm_campaign: null,
          utm_medium: null,
          utm_term: null,
          utm_content: null
        }
      }
    }
  }
</script>
